import Head from 'next/head';
import type { NextPage } from 'next';
import { Image } from 'react-datocms';
import { env } from '@zopauk/nextjs-env';
import { renderMetaTags } from 'react-datocms';
import { withSession } from '@zopauk/nextjs-auth';
import { SimpleHero } from '@/components/SimpleHero';
import PreFooter from '@/components/Modules/PreFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { FlexContainer } from '@zopauk/react-components';
import { HubNavigation } from '@/components/HubNavigation';
import { useCollectionQuery } from '@/utils/dato/generated';
import { StructuredData } from '@/components/StructuredData';
import { HelpBackButton } from '@/components/HelpBackButton';
import { type CollectionContext, serverSideProps } from '@/utils/models/collection';
import { getCollectionBreadcrumbs, buildBreadcrumbSchema } from '@/utils/breadcrumbs';

const Collection: NextPage<CollectionContext> = (context) => {
  const { data } = useCollectionQuery({ slug: context.slug });

  if (data?.collection) {
    const collection = data.collection;

    const breadcrumbs = getCollectionBreadcrumbs(collection);

    const pathname = ['financial-support'].includes(collection.hub.slug)
      ? `/contact/${collection.hub?.slug}`
      : '/contact';

    return (
      <>
        <Head>
          {collection?.seo ? renderMetaTags(collection.seo.concat(data.site.favicon)) : null}
          <StructuredData structuredData={buildBreadcrumbSchema(breadcrumbs)} />
        </Head>
        <FlexContainer className="mt-8">
          <Breadcrumbs breadcrumbs={breadcrumbs} className="mb-6" />

          <HelpBackButton />

          <SimpleHero
            image={
              collection.hub?.featuredImage?.responsiveImage ? (
                <Image
                  data={collection.hub.featuredImage.responsiveImage}
                  style={{ margin: '0 auto' }}
                  objectPosition="50% 50%"
                  priority={true}
                />
              ) : undefined
            }
            description="Find answers to our most frequently asked questions."
            title={collection.hub?.title || 'Untitled'}
          />

          {collection.hub ? (
            <HubNavigation
              collections={collection.hub?.collections}
              questions={collection.questions}
              title={collection.title}
              hub={collection.hub}
            />
          ) : null}

          <PreFooter
            preFooterImage={{
              responsiveImage: {
                base64:
                  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAVCAMAAABrN94UAAACzVBMVEVyYmZzYWZzYWZzYmZ0YGZ0YGZ0YGZ0YGZ0YGZ0YGZ0YWZ1X2Z1X2Z1X2Z1X2Z1X2Z1X2Z1X2Z1YGZ1YGZ1YGZ1YGZ2XmZ2XmZ2XmZ2XmZ2XmZ2XmZ2XmZ2X2Z2X2Z2X2Z3XWZ3XWZ3XWZ3XWZ3XWZ3XWZ3XWZ3XWZ3XWZ3XmZ3XmZ3XmZ4XGZ4XGZ4XGZ4XGZ4XGZ4XGZ4XGZ4XGZ4XGZ4XGZ4XWZ4XWZ4XWZ5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5W2Z5XGZ5XGZ5XGZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6WmZ6W2Z6W2Z6W2Z7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WWZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ7WmZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WGZ8WWZ8WWZ8WWZ8WWZ8WWZ8WWZ8WWZ8WWZ9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9V2Z9WGZ9WGZ9WGZ9WGZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+VmZ+V2Z+V2Z+V2Z+V2Z+V2Z/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VWZ/VmZ/VmZ/VmaAVGaAVGaAVGaAVGaAVGaAVWaAVWaAVWaAVWZ6QAafAAAA73RSTlMAAAYAAAQJChASAAAGDxwrLTEAGR0gABguLz0+QwAGHgAYJyg+QE5QVAANFgACIDg9UFZcX2MINEwABgsmREheX21wcQAaaAABChESMjk6SlFVZWt4eoIAKisABRQXGyUsMDQ2Nzs9Q0VHTU9RUlNUWlxgYWJjZmdsdoCCiwASFRoeNjg5VlhZaWoADjhUbnN0dnx+gYOGi42QlJWWl5uipCpdYGFkanCKAAobL0hje4qbpLKzuLrCxsjN1R6jpqcjLUBYaoOaqrvDytLe5RFyq9LcNkBJU2V1fIuYpLC2usXM3FKU1mZtb3aIWWOBnscclfUAAAFSSURBVHjaRZBLTwIxFIXvaYujw4wgLx+YKIqsXSq/nwSDLnRnJCaAGlGHyfjgMUpb2wLaxbnt+XrvaSo4kaTl4sqVjW8jwuz5Cix9p+LvvFruvgGWT4lAeukufBK2cgKHtI3O3Uwt8BxnHtSX3CSyXuo6slbX8Q7lKedQwRHh28kszkIp7WIWDSQCI34UMAs8R6YLEBoJUsahMvpV/79amECWSxkgIhXqfyKKRA1hQRwqrXwarsApoXJxnGHdLSX9wZjCFThA4bqOtWFDqdvP/JzygyU4uU9qwMuhDm9q42pbJyY0b8FHBQj6uxiN97davVKtY8zEhSPXn1T8x1LQjZot3SsddRajGL7K2YdZOZ7Uu6NmO5X9M31lfltUEGG284a94bR+Fzcv5upJn6ED8cPKMVB9/mF84umoeaklou1zCEAWE0jQ9pAzLpgnOdh7Ab+jqoj1HQ6uwgAAAABJRU5ErkJggg==',
                srcSet:
                  'https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&dpr=0.25&fit=crop&h=580 169w,https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&dpr=0.5&fit=crop&h=580 338w,https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&dpr=0.75&fit=crop&h=580 507w,https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&fit=crop&h=580 676w,https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&dpr=1.5&fit=crop&h=580 1014w',
                src: 'https://www.datocms-assets.com/23873/1679569061-support-technician.png?auto=format&fit=crop&h=580',
                alt: 'Man on a computer with a headset smiling',
                sizes: '(max-width: 676px) 100vw, 676px',
                aspectRatio: 1.1655172413793105,
                __typename: 'ResponsiveImage',
                webpSrcSet: '',
                height: 580,
                title: null,
                width: 676,
              },
              __typename: 'AltFileField',
            }}
            body={{
              value: {
                document: {
                  children: [
                    {
                      children: [
                        {
                          value: 'Need to get in touch?',
                          type: 'span',
                        },
                      ],
                      type: 'heading',
                      level: 2,
                    },
                    {
                      children: [
                        {
                          value: 'Our team of helpful humans are on hand.',
                          type: 'span',
                        },
                      ],
                      type: 'paragraph',
                    },
                    {
                      item: '136535904',
                      type: 'block',
                    },
                  ],
                  type: 'root',
                },
                schema: 'dast',
              },
              preFooterBodyBlocks: [
                {
                  href: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL') || 'https://www.zopa.com'}${pathname}`,
                  __typename: 'ButtonRecord',
                  text: 'Contact us',
                  style: 'primary',
                  id: '136535904',
                  newTab: false,
                },
              ],
              __typename: 'PrefooterModelBodyField',
              links: [],
            }}
            backgroundColor={{ __typename: 'ColorField', hex: '#E4F9F1' }}
            __typename="PrefooterRecord"
            headerVisibility={false}
            fullWidth={false}
            flipped={false}
            id="prefooter"
          />
        </FlexContainer>
      </>
    );
  }

  return null;
};

export const getServerSideProps = serverSideProps;

export default withSession(Collection, { forceLogin: false });
